import axios from 'axios';

const s3 = axios.create();
delete s3.defaults.headers.common.Authorization;

const UploadService = {
  async uploadVideoToS3(file, setUploadProgress) {
    try {
      // Update progress to 5% when presigned URL is being fetched
      if (setUploadProgress) {
        setUploadProgress(5);
      }

      const presignedPostUrl = await axios.get('/uploads/presign', {
        params: { fileSize: file.size },
      });
      
      // Update progress to 10% when presigned URL is received
      if (setUploadProgress) {
        setUploadProgress(10);
      }

      const formData = new FormData();
      Object.keys(presignedPostUrl.data.fields).forEach((key) => {
        formData.append(key, presignedPostUrl.data.fields[key]);
      });

      formData.append('file', file);

      await s3.post(presignedPostUrl.data.url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            // Map 10-100% for the actual upload (10% is already set above)
            const uploadProgress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 90
            );
            const totalProgress = 10 + uploadProgress; // 10% + upload progress

            if (setUploadProgress) {
              setUploadProgress(totalProgress);
            }
          }
        },
      });

      // Ensure we set 100% on completion
      if (setUploadProgress) {
        setUploadProgress(100);
      }

      return presignedPostUrl.data.fields.key; // Return the key of the uploaded file
    } catch (error) {
      // Reset progress on error
      if (setUploadProgress) {
        setUploadProgress(0);
      }
      throw error;
    }
  },

  async uploadVideoToS3InParts(file, setUploadProgress) {
    try {
      const MIN_PART_SIZE = 5 * 1024 * 1024; // 5 MB (S3 minimum)
      const TARGET_PART_SIZE = 25 * 1024 * 1024; // 25 MB (balance throughput vs. request overhead)
      const MAX_PARTS = 50; // Keep request count reasonable for mobile
      const MAX_PARALLEL_PARTS = 4; // Avoid saturating mobile radios/browsers
      const totalSize = file.size;
      
      // Update progress to 5% when starting multipart upload
      if (setUploadProgress) {
        setUploadProgress(5);
      }

      // Compute parts using a target size while respecting S3 minimums
      let partCount = Math.ceil(totalSize / TARGET_PART_SIZE);
      partCount = Math.max(partCount, 2);
      partCount = Math.min(partCount, MAX_PARTS);
      // Ensure each computed part (except last) is at least 5MB
      while (partCount > 1 && Math.ceil(totalSize / partCount) < MIN_PART_SIZE) {
        partCount -= 1;
      }
      // If fewer than 2 parts, use single-part upload
      if (partCount < 2) {
        return this.uploadVideoToS3(file, setUploadProgress);
      }

      // Update progress to 10% when initiating multipart session
      if (setUploadProgress) {
        setUploadProgress(10);
      }

      const { data: session } = await axios.post('/uploads/multipart/initiate', {
        partCount,
        fileSize: totalSize,
      });
      const { uploadId, s3FileKey, parts } = session;

      const partSize = Math.ceil(totalSize / partCount);
      const loadedPerPart = new Array(parts.length).fill(0);

      const uploadPart = ({ partNumber, url }, idx) => {
        const start = (partNumber - 1) * partSize;
        const end =
          partNumber === parts.length ? totalSize : start + partSize;
        const chunk = file.slice(start, end);

        return s3
          .put(url, chunk, {
            headers: {
              'Content-Type': 'application/octet-stream',
            },
            onUploadProgress: (evt) => {
              loadedPerPart[idx] = evt.loaded;
              if (setUploadProgress) {
                const loadedSoFar = loadedPerPart.reduce((sum, v) => sum + v, 0);
                // Map 10-90% for parts upload (10% is already set above)
                const uploadProgress = Math.round((loadedSoFar / totalSize) * 80);
                const totalProgress = 10 + uploadProgress; // 10% + upload progress
                setUploadProgress(totalProgress);
              }
            },
          })
          .then((res) => {
            return {
              partNumber,
              eTag: res.headers.etag, // axios lower-cases headers
            };
          });
      };

      // Simple concurrency limiter to avoid too many parallel requests
      const runWithConcurrency = async (items, limit, handler) => {
        const results = [];
        let index = 0;
        const worker = async () => {
          const current = index;
          index += 1;
          if (current >= items.length) {
            return undefined;
          }
          results[current] = await handler(items[current], current);
          return worker();
        };
        const workers = new Array(Math.min(limit, items.length)).fill(null).map(worker);
        await Promise.all(workers);
        return results;
      };

      const partsMeta = await runWithConcurrency(parts, MAX_PARALLEL_PARTS, uploadPart);

      // Update progress to 90% when completing multipart upload
      if (setUploadProgress) {
        setUploadProgress(90);
      }

      await axios.post('/uploads/multipart/complete', {
        uploadId,
        s3FileKey,
        parts: partsMeta,
      });

      // Ensure we set 100% on completion
      if (setUploadProgress) {
        setUploadProgress(100);
      }

      return s3FileKey;
    } catch (error) {
      // Reset progress on error
      if (setUploadProgress) {
        setUploadProgress(0);
      }
      throw error;
    }
  },
};

export default UploadService;
